import {
  type CreateShare,
  type Share,
  type ShareCreated,
  type ShareExpiry,
  shareCreatedSchema,
  shareListSchema,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';
import { LIBRARY_ID } from '../gallery/config';

/**
 * Client des routes AUTHENTIFIÉES de partage (création, gouvernance).
 *
 * À ne pas confondre avec `publicshare/api.ts`, qui sert la page publique et
 * n'utilise DÉLIBÉRÉMENT pas `apiFetch`. Ici c'est l'inverse : ces appels sont faits
 * par un compte connecté, ils DOIVENT porter le slot du multi-login et bénéficier du
 * traitement des 401 — c'est le chemin normal de toute l'application.
 */

/** Ce qu'on demande à partager, tel que le déduit l'écran d'où part le clic. */
export type ShareTarget =
  /** Une photo, ou une sélection multiple — figée à la création. */
  | { kind: 'selection'; assetIds: string[] }
  /** Un dossier, RÉCURSIF. `''` = toute la bibliothèque visible. */
  | { kind: 'folder'; folderPath: string };

export class ShareApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ShareApiError';
  }
}

async function parseError(res: Response): Promise<never> {
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: { message?: string } };
    if (body.error?.message) message = body.error.message;
  } catch {
    // Corps non JSON : le statut suffit à décider quoi montrer.
  }
  throw new ShareApiError(res.status, message);
}

/**
 * Crée un partage. La réponse porte le jeton BRUT — la seule et unique fois où il
 * existe en clair. L'appelant doit l'afficher immédiatement.
 */
export async function createShare(
  target: ShareTarget,
  input: { title: string; expiresIn: ShareExpiry; shareOriginals: boolean },
): Promise<ShareCreated> {
  const body: CreateShare = {
    title: input.title,
    expiresIn: input.expiresIn,
    shareOriginals: input.shareOriginals,
    ...(target.kind === 'folder'
      ? { kind: 'folder' as const, folderPath: target.folderPath }
      : { kind: 'selection' as const, assetIds: target.assetIds }),
  };
  const res = await apiFetch(`/api/libraries/${LIBRARY_ID}/shares`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await parseError(res);
  return shareCreatedSchema.parse(await res.json());
}

/** Mes partages (écran « Mes partages » des Paramètres). */
export async function listMyShares(): Promise<Share[]> {
  const res = await apiFetch('/api/shares/mine');
  if (!res.ok) await parseError(res);
  return shareListSchema.parse(await res.json()).shares;
}

/** Tous les partages de la bibliothèque (console admin). */
export async function listAllShares(): Promise<Share[]> {
  const res = await apiFetch(`/api/libraries/${LIBRARY_ID}/admin/shares`);
  if (!res.ok) await parseError(res);
  return shareListSchema.parse(await res.json()).shares;
}

/** Révocation douce : le lien meurt, la ligne reste (historique, compteur de vues). */
export async function revokeShare(id: string): Promise<void> {
  const res = await apiFetch(`/api/shares/${id}`, { method: 'DELETE' });
  if (!res.ok) await parseError(res);
}

/** URL publique d'un partage, à partir du jeton rendu une seule fois. */
export function shareUrl(secret: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}/p/${encodeURIComponent(secret)}`;
}
